<template>
   <v-dialog max-width="500" persistent :retain-focus="false" v-model="dialogVisible">
    <v-card class="confirm-dialog">
      <v-card-title><span>提示</span></v-card-title>
      <v-card-text><span>发现你的系统版本不是最新,现在将刷新更新。</span></v-card-text>
      <v-card-actions>
        <v-btn color="primary" variant="flat" @click="handleImmediateRefresh">确定</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <!-- <v-dialog
    v-model="dialogVisible"
    max-width="500"
    persistent
    :retain-focus="false"
  >
    <v-card>
      <v-card-title class="d-flex align-center dialog-top-bg">
        <v-icon
          icon="mdi-update"
          color="primary"
          class="mr-3"
        />
        版本更新提醒
      </v-card-title>
      
      <v-card-text class="pt-4">
        <div class="version-info">
          <p class="text-body-1 mb-3">
            {{ updateMessage }}
          </p>
          
          <v-alert
            v-if="countdown > 0"
            type="warning"
            variant="tonal"
            class="mb-3"
          >
            <template #prepend>
              <v-icon>mdi-timer-outline</v-icon>
            </template>
            {{ countdown }} 秒后自动刷新页面
          </v-alert>
          
          <v-divider class="my-3" />
          
          <div class="version-details">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="text-body-2 text-medium-emphasis">当前版本：</span>
              <v-chip
                :color="isNewVersion ? 'success' : 'warning'"
                size="small"
                variant="outlined"
              >
                {{ currentVersion }}
              </v-chip>
            </div>
            
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="text-body-2 text-medium-emphasis">缓存版本：</span>
              <v-chip
                color="info"
                size="small"
                variant="outlined"
              >
                {{ cachedVersion }}
              </v-chip>
            </div>
            
            <div class="d-flex justify-space-between align-center">
              <span class="text-body-2 text-medium-emphasis">构建时间：</span>
              <span class="text-body-2">{{ formatBuildTime }}</span>
            </div>
          </div>
        </div>
      </v-card-text>
      
      <v-card-actions class="pa-4">
        <v-spacer />
        
        <v-btn
          color="grey"
          variant="tonal"
          style="margin-right: 8px"
          @click="handleClose"
        >
          稍后刷新
        </v-btn>
        
        <v-btn
          color="warning"
          variant="flat"
          style="margin-right: 8px"
          @click="handleForceRefresh"
        >
          3秒后刷新
        </v-btn>
        
        <v-btn
          color="primary"
          variant="flat"
          @click="handleImmediateRefresh"
        >
          立即刷新
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog> -->
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useSnackbar } from '@/composables/useSnackbar'
import {
  getCurrentVersion,
  getCurrentBuildTime,
  getCachedVersion,
  updateVersionCache,
  getVersionUpdateMessage,
  compareVersions
} from '@/utils/versionUtils'

// Props
interface Props {
  visible: boolean
  onClose?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  visible: false
})

// Emits
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'forceRefresh'): void
  (e: 'immediateRefresh'): void
}>()

// 组件状态
const router = useRouter()
const userStore = useUserStore()
const { showInfo } = useSnackbar()

const dialogVisible = ref(false)
const currentVersion = ref('')
const cachedVersion = ref('')
const buildTime = ref('')
const updateMessage = ref('')
const isNewVersion = ref(false)
const countdown = ref(30) // 倒计时秒数
let countdownTimer: ReturnType<typeof setInterval> | null = null

// 计算属性
const formatBuildTime = computed(() => {
  if (!buildTime.value) return ''
  try {
    const date = new Date(buildTime.value)
    return date.toLocaleString('zh-CN')
  } catch {
    return buildTime.value
  }
})

// 监听visible变化
watch(() => props.visible, (newVal) => {
  if (newVal) {
    showDialog()
  } else {
    dialogVisible.value = false
  }
})

// 监听对话框状态
watch(dialogVisible, (newVal) => {
  if (!newVal) {
    emit('close')
  }
})

// 显示对话框
function showDialog() {
  // 获取版本信息
  currentVersion.value = getCurrentVersion()
  buildTime.value = getCurrentBuildTime()
  
  const cached = getCachedVersion()
  if (cached) {
    cachedVersion.value = cached.version
    
    // 检查版本更新
    const hasUpdate = cached.version !== currentVersion.value
    if (hasUpdate) {
      const comparison = compareVersions(currentVersion.value, cached.version)
      isNewVersion.value = comparison > 0
      updateMessage.value = getVersionUpdateMessage(cached.version, currentVersion.value)
    }
  }
  
  // 重置倒计时
  countdown.value = 30
  startCountdown()
  
  dialogVisible.value = true
}

// 开始倒计时
function startCountdown() {
  countdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownTimer!)
      countdownTimer = null
    }
  }, 1000)
}

// 停止倒计时
function stopCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

// 处理关闭
function handleClose() {
  stopCountdown()
  dialogVisible.value = false
  emit('close')
}

// 处理延迟刷新
function handleForceRefresh() {
  // stopCountdown()
  // dialogVisible.value = false
  // showInfo('页面将在3秒后自动刷新...')
  // emit('forceRefresh')
}

// 处理立即刷新
function handleImmediateRefresh() {
  stopCountdown()
  dialogVisible.value = false
  showInfo('正在刷新页面...')
  emit('immediateRefresh')
}
</script>

<style lang="scss" scoped>
.version-info {
  .version-details {
    background-color: rgb(var(--v-theme-surface-variant));
    border-radius: 8px;
    padding: 16px;
  }
}

.dialog-top-bg {
  background: linear-gradient(135deg, #D2475D, #D24760);
  color: white;
  padding: 16px;
}

.dialog-top-bg :deep(.mdi){
  color: white!important;
}

.v-btn.bg-primary.v-btn--variant-flat {
  background: var(--primary-gradient) !important;
  border: none !important;
  box-shadow: 0 2px 4px #D2475D !important;
}

.text-error {
  color: rgb(var(--v-theme-error)) !important;
  background: rgba(47,34,37, 0.7);
}

:deep(.confirm-dialog) {
  background-color: #2B2D33 !important;
}

:deep(.confirm-dialog .v-card-title) {
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  padding: 16px;
}

:deep(.confirm-dialog .v-card-text) {
  color: rgba(255, 255, 255, 0.7);
  padding: 16px;
}

:deep(.confirm-dialog .v-card-actions) {
  padding: 8px 16px 16px;
}
</style>
